import type { FloristInfo } from '~/app/types/florist/floristInfoType';

import type {
  FloristInfoRequest,
  FloristPhoneNumberRequest,
  FloristSecondEmailRequest,
  PersonalCodeRequest,
} from '~/app/types/florist/floristInfoRequest';
import type { AbstractRequest } from '~/app/types/abstractRequest';
import { useRawCustomFetch } from '~/composables/useCustomFetch';
import type { FetchResponse } from 'ofetch/node';
import type { AllFlorists } from '~/app/types/florist/allFloristsType';

export interface OpeningHoursRequest extends AbstractRequest {
  dayOfWeek: number;
  isClosed: boolean;
  isOpenWholeDay: boolean;
  isOpenOnlyMorning: boolean;
  isOpenOnlyAfternoon: boolean;
  openHour: string;
  closeHour: string;
  isBreakTimePresent: boolean;
  breakTimeStart: string;
  breakTimeEnd: string;
}

export interface UserInfo {
  defaultShop: string;
  role: number;
  startDate: Date;
  endDate: Date;
  color: string;
  username: string;
  lastProjectedIds: string[];
  lastUpdate: Date;
  id: string;
  createdAt: Date;
}

export const emptyUserInfo: UserInfo = {
  defaultShop: '',
  role: 0,
  startDate: new Date(0),
  endDate: new Date(0),
  color: '',
  username: '',
  lastProjectedIds: [],
  lastUpdate: new Date(0),
  id: '',
  createdAt: new Date(0),
};

export async function fetchFloristInfo(floristId: string) {
  const url = `${useMsUrls().floristQueriesBaseUrl}/GetFloristById?floristId=${floristId}`;
  return await useCustomFetch<FloristInfo>(url);
}

export async function fetchAndSyncFloristInfo(floristId: string) {
  try {
    const data = await fetchFloristInfo(floristId);
    useFloristInfoStore().setFloristInfo(data);
  } catch (e) {
    console.error('fetchFloristInfo error', e);
  }
}

export async function updateFloristContact(request: FloristInfoRequest) {
  await useCustomFetch(`${useMsUrls().floristInputBaseUrl}/AddContact`, {
    method: 'POST',
    body: request,
  });
}

export async function updateFloristPhoneNumber(request: FloristPhoneNumberRequest): Promise<FetchResponse<any>> {
  return await useRawCustomFetch(`${useMsUrls().floristInputBaseUrl}/UpdatePhoneNumber`, {
    method: 'POST',
    body: request,
  });
}

export async function updateSecondEmailNumber(request: FloristSecondEmailRequest): Promise<FetchResponse<any>> {
  return await useRawCustomFetch(`${useMsUrls().floristInputBaseUrl}/UpdateEmail`, {
    method: 'POST',
    body: request,
  });
}

export async function updateFloristPersonalCode(request: PersonalCodeRequest): Promise<FetchResponse<any>> {
  return await useRawCustomFetch(`${useMsUrls().floristInputBaseUrl}/DefinePersonalCode`, {
    method: 'POST',
    body: request,
  });
}

export async function updateFloristOpeningHours(request: OpeningHoursRequest) {
  await useCustomFetch(`${useMsUrls().floristInputBaseUrl}/DefineOpeningHours`, {
    method: 'POST',
    body: request,
  });
}

export async function getAllFlorists() {
  const config = useRuntimeConfig();
  const url = `${useMsUrls().floristQueriesBaseUrl}/GetAllFlorists`;
  return await useCustomFetch<AllFlorists[]>(url, {
    timeout: config.public.timeoutSlowQueries,
  });
}

export async function getBasic() {
  const config = useRuntimeConfig();
  const url = `${config.public.apiBaseUrl}/itfloristqueries/bo`;
  return await $fetch<string>(url, {
    responseType: 'text',
    timeout: config.public.timeoutSlowQueries,
  });
}

export async function getUserById(userId: string) {
  const url = `${useMsUrls().floristQueriesBaseUrl}/GetUserById/${userId}`;
  return await useCustomFetch<UserInfo>(url);
}
